import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight, Loader2, Megaphone } from "lucide-react";
import type { CampaignStatusName } from "@multi-chat/shared";
import { ActiveFilterBar, activeFilterChip } from "../../components/common/ActiveFilterBar/ActiveFilterBar.js";
import { WorkspaceDrawerTrigger } from "../../components/layout/WorkspaceDrawerTrigger.js";
import { Button } from "../../components/ui/button/index.js";
import { CampaignStatusBadge } from "./CampaignStatusBadge.js";
import { campaignShellStatusLabelKey } from "./campaign-draft-workflow.js";
import { CAMPAIGN_LIST_NAV_ITEMS, campaignListHref } from "./campaigns-presentation.js";
import { useCampaignWorkspace } from "./CampaignWorkspaceLayout.js";

export function CampaignHeader({
  name,
  status,
  confirmed,
  recipientCount,
  saving,
  actions,
  onOpenMobileNav,
}: {
  name: string;
  status: CampaignStatusName;
  confirmed: boolean;
  recipientCount: number;
  saving?: boolean;
  actions?: React.ReactNode;
  onOpenMobileNav: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { list, mobileNavOpen } = useCampaignWorkspace();
  const statusModeLabel = t(campaignShellStatusLabelKey(status));
  const listHref = campaignListHref({
    status: list.status,
    search: list.search,
    page: list.page,
    pageSize: list.pageSize,
    sort: list.sort,
  });

  return (
    <div
      className="flex min-w-0 flex-col gap-2"
      data-testid="campaign-header"
    >
      <div className="flex min-w-0 flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex min-w-0 items-start gap-2">
        <span
          className="inline-grid size-8 shrink-0 place-items-center rounded-lg border border-line/70 bg-surface text-accent"
          aria-hidden="true"
        >
          <Megaphone size={15} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1 text-[0.7rem] text-muted">
            <Link
              to={listHref}
              className="shrink-0 text-muted no-underline transition-colors hover:text-foreground"
              data-testid="campaign-back"
              aria-label={t("campaigns.actions.backToCampaigns")}
            >
              {t("campaigns.detail.breadcrumb")}
            </Link>
            <ChevronRight
              size={12}
              className="shrink-0 opacity-60 rtl:-scale-x-100"
              aria-hidden="true"
            />
            <h1 className="m-0 min-w-0 truncate text-[0.9375rem] font-semibold tracking-[-0.02em] text-foreground">
              {name}
            </h1>
          </div>
          <p className="m-0 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[0.75rem] text-muted">
            <span className="sr-only">{statusModeLabel}</span>
            <CampaignStatusBadge status={status} />
            <span aria-hidden="true">·</span>
            <span>
              {confirmed
                ? t("campaigns.detail.recipientsCount", { count: recipientCount })
                : t("campaigns.detail.audienceNotConfirmed")}
            </span>
          </p>
        </div>
      </div>
      <div className="flex min-w-0 w-full flex-wrap items-center gap-2 lg:w-auto lg:justify-end">
        <WorkspaceDrawerTrigger
          label={t("campaigns.nav.all")}
          open={mobileNavOpen}
          onClick={onOpenMobileNav}
          testId="campaign-mobile-nav-trigger"
          ariaLabel={t("campaigns.nav.open")}
        />
        {saving ? (
            <p className="flex items-center gap-2 text-[0.75rem] text-muted" aria-live="polite">
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
              {t("campaigns.saving")}
            </p>
          ) : null}
          {actions}
      </div>
      </div>
      <ActiveFilterBar
        className="rounded-xl border border-line/50"
        chips={[
          activeFilterChip(
            "status",
            t("campaigns.nav.filters"),
            list.status
              ? t(
                  CAMPAIGN_LIST_NAV_ITEMS.find((item) => item.status === list.status)?.labelKey ??
                    "campaigns.nav.filters",
                )
              : null,
            () =>
              navigate(
                campaignListHref({
                  search: list.search,
                  page: 1,
                  pageSize: list.pageSize,
                  sort: list.sort,
                }),
              ),
          ),
        ].flatMap((chip) => (chip ? [chip] : []))}
        onClearAll={() =>
          navigate(
            campaignListHref({
              search: "",
              page: 1,
              pageSize: list.pageSize,
              sort: list.sort,
            }),
          )
        }
      />
    </div>
  );
}
